import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { HandIcon, PaintIcon, RubberIcon } from "@l2r-front/l2r-icons"
import {
    REFERENTIAL_MAP_ID,
    Legend,
    Draw,
    FREE_ROAM_MODE,
    PAINT_MODE,
    RUBBER_MODE,
    LateralizedFreeRoamMode,
    LateralizedStabiloMode,
    LateralizedRubberMode,
    useMapDispatchContext,
} from "@l2r-front/l2r-map"
import { NetworkReferentialLayer, ReferentialMap, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { RoadsideInventory } from "@l2r-front/l2r-proptypes"
import { UiModal, useIsMobileDevice, useTheme } from "@l2r-front/l2r-ui"
import { CatalogItem } from "@l2r-front/l2r-utils"

import { API_MODULE_ROADSIDE } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadsideStateContext } from "../../contexts/RoadsideContext"
import { useRoadsideType } from "../../hooks/queries/useRoadsideTypes"
import { useRoadRoadsideInventoriesFeatures } from "../../hooks/utils/useRoadRoadsideInventoriesFeatures"

import { RoadRoadsideInventoriesLayer } from "../RoadRoadsideInventoriesLayer"

import {
    createInventoryItem,
    groupFeaturesByDirection,
    haveFeaturesChanged,
    intersectRanges,
    subtractMultipleRanges,
} from "./RoadsidePresenceEditModal.helper"
import * as Styled from "./RoadsidePresenceEditModal.styled"

type RoadsidePresenceEditModalProps = {
    className?: string,
    open: boolean,
    type: CatalogItem,
    onClose: () => void,
    onSave: (inventoryItems: RoadsideInventory[], parentType: string) => void,
    showLoader?: boolean,
}

export function RoadsidePresenceEditModal(props: RoadsidePresenceEditModalProps) {

    const {
        className,
        open,
        onSave,
        type,
        onClose,
        showLoader = false,
    } = props

    const [saveEnable, setSaveEnable] = useState(false)
    const [inventoryItems, setInventoryItems] = useState<RoadsideInventory[]>([])
    const { selectedSubNetworkSlugs } = useNetworksStateContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const theme = useTheme()
    const { road } = useParams()
    const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null)
    const { ascendantCodes } = useRoadsideStateContext()
    const { getMapRef } = useMapDispatchContext()
    const mapRef = getMapRef(REFERENTIAL_MAP_ID)
    const [mapZoom, setMapZoom] = useState(0)

    const { data: parentType } = useRoadsideType(type.parent)

    const parentTypeName = parentType?.name || ""

    const baseFeatures = useRoadRoadsideInventoriesFeatures(road, type.parent, {})
    const roadFeatures = useMemo(() => {
        if (!baseFeatures) return null
        return {
            type: "FeatureCollection",
            features: baseFeatures.features.filter(feature => !feature.properties?.["roadsideInventoryType"] && !feature.properties?.["locked"])
        }
    }, [baseFeatures])
    const typeFeatures = useMemo(() => {
        if (!baseFeatures) return null
        return {
            type: "FeatureCollection",
            features: baseFeatures.features.filter(feature => feature.properties?.["roadsideInventoryType"] === type.code)
        }
    }, [baseFeatures, type])
    const otherTypesFeatures = useMemo(() => {
        if (!baseFeatures) return null
        return {
            type: "FeatureCollection",
            features: baseFeatures.features.filter(feature => feature.properties?.["roadsideInventoryType"] && feature.properties?.["roadsideInventoryType"] !== type.code)
        }
    }, [baseFeatures, type])

    const handleFeaturesChanged = useCallback((featureCollection: GeoJSON.FeatureCollection) => {
        const result: RoadsideInventory[] = []

        // Get features from featureCollection (drawn features)
        const drawnFeatures = featureCollection.features || []
        const drawnByDirection = groupFeaturesByDirection(drawnFeatures)

        // Get typeFeatures (existing features of current type)
        const existingTypeFeatures = typeFeatures?.features || []
        const typeByDirection = groupFeaturesByDirection(existingTypeFeatures)

        // Get otherTypesFeatures
        const existingOtherTypesFeatures = otherTypesFeatures?.features || []
        const otherTypesByDirection = groupFeaturesByDirection(existingOtherTypesFeatures)

        // Process each direction separately
        for (const direction of ["+", "-"] as const) {
            const drawnInDirection = drawnByDirection[direction]
            const typeInDirection = typeByDirection[direction]
            const otherTypesInDirection = otherTypesByDirection[direction]

            // Get all drawn ranges for this direction
            const drawnRanges: [number, number][] = drawnInDirection.map(d => d.normalizedRange)

            // 1. typeFeatures: Keep only parts overlapping with featureCollection.features
            for (const typeItem of typeInDirection) {
                const { linearLoc, normalizedRange, feature } = typeItem
                const roadsideInventoryType = feature.properties?.["roadsideInventoryType"] || type.code
                const condition = feature.properties?.["condition"] ?? 1

                for (const drawnRange of drawnRanges) {
                    const intersection = intersectRanges(normalizedRange, drawnRange)
                    if (intersection) {
                        result.push(createInventoryItem(
                            linearLoc.road,
                            intersection[0],
                            intersection[1],
                            direction,
                            roadsideInventoryType,
                            condition
                        ))
                    }
                }
            }

            // 2. featureCollection.features: Keep only parts that DON'T overlap with typeFeatures
            // In this case, roadside_inventory_type = type.code and condition = 1
            const typeRanges: [number, number][] = typeInDirection.map(t => t.normalizedRange)

            for (const drawnItem of drawnInDirection) {
                const { linearLoc, normalizedRange } = drawnItem
                const remainingRanges = subtractMultipleRanges(normalizedRange, typeRanges)

                for (const remaining of remainingRanges) {
                    result.push(createInventoryItem(
                        linearLoc.road,
                        remaining[0],
                        remaining[1],
                        direction,
                        type.code,
                        1
                    ))
                }
            }

            // 3. otherTypesFeatures: Keep only parts that DON'T overlap with featureCollection.features
            for (const otherTypeItem of otherTypesInDirection) {
                const { linearLoc, normalizedRange, feature } = otherTypeItem
                const roadsideInventoryType = feature.properties?.["roadsideInventoryType"] || ""
                const condition = feature.properties?.["condition"] ?? 1

                const remainingRanges = subtractMultipleRanges(normalizedRange, drawnRanges)

                for (const remaining of remainingRanges) {
                    result.push(createInventoryItem(
                        linearLoc.road,
                        remaining[0],
                        remaining[1],
                        direction,
                        roadsideInventoryType,
                        condition
                    ))
                }
            }
        }

        setInventoryItems(result)

        // Compare result features with typeFeatures to check if there are changes
        const hasChanges = haveFeaturesChanged(typeFeatures?.features || [], result, type.code)
        setSaveEnable(hasChanges)
    }, [typeFeatures, otherTypesFeatures, type.code])

    const handleMapZoom = useCallback(() => {
        const zoom = mapRef?.getMap().getZoom() || 0
        setMapZoom(zoom)
    }, [mapRef])

    const handleSave = useCallback(() => {
        onSave(inventoryItems, type.parent)
    }, [onSave, inventoryItems, type.parent])

    const buttons = useMemo(() => {
        return <>
            <Styled.Button id={"button-cancel"} isMobile={isMobile} variant="tertiary" onClick={onClose}>{t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.cancelButton")}</Styled.Button>
            <Styled.Button id={"button-ok"} isMobile={isMobile} disabled={!saveEnable} onClick={handleSave}>{t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.saveButton")}</Styled.Button>
        </>
    }, [handleSave, saveEnable, isMobile, onClose, t])

    const modesConfig = {
        [FREE_ROAM_MODE]: {
            mode: LateralizedFreeRoamMode,
            icon: <HandIcon />,
            brushes: [{ name: "black", token: "common/black_states" }],
        },
        [PAINT_MODE]: {
            mode: LateralizedStabiloMode,
            icon: <PaintIcon />,
            brushes: [
                { name: "blue", token: "_components/legend/referential/trace" },
            ],
        },
        [RUBBER_MODE]: {
            mode: LateralizedRubberMode,
            icon: <RubberIcon />,
            brushes: [{ name: "black", token: "common/black_states" }],
        },
    }

    const isCategoryType = ascendantCodes.length === 2

    const displayedLegends = useMemo(() => {
        const legendData = [
            {
                color: theme.palette["_components/legend/types_of_works/L"].main,
                label: t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.legend.presence"),
            },
        ]

        if (!isCategoryType) {
            legendData.push(
                {
                    color: theme.palette["_components/legend/referential/other"].main,
                    label: t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.legend.otherType", { parentType: parentTypeName }),
                },
                {
                    color: theme.palette["_components/legend/referential/B"].main,
                    label: t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.legend.replacing"),
                },
            )
        }
        return [{
            layerName: "roadsidePresence",
            title: t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.legend.title"),
            legendData: legendData,
        }]
    }, [t, parentTypeName, isCategoryType, theme])

    const stabiloLayerStyle =
        [
            "case",
            ["all",
                ["==", ["typeof", ["get", "roadsideInventoryType", ["properties"]]], "string"],
                ["!=", ["get", "roadsideInventoryType", ["properties"]], ["literal", type.code]],
            ],
            theme.palette["_components/legend/referential/B"].main,
            theme.palette["_components/legend/types_of_works/L"].main,
        ]

    return (
        <UiModal
            buttons={buttons}
            className={className}
            desc={t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.desc")}
            open={open}
            onClose={onClose}
            showLoader={showLoader}
            title={t(I18N_NAMESPACE, "container.roadsidePresenceEditModal.title", { type: `${parentTypeName} / ${type?.["name"]}` })}
        >
            <ReferentialMap
                topLeftElements={[<Styled.DrawContainer key="draw-container" ref={setContainerElement} />, <Legend key="city-map-legend" displayedLegends={displayedLegends} openByDefault={!isMobile} />]}
                onZoom={handleMapZoom}
            >
                <NetworkReferentialLayer
                    interactive={false}
                    mapId={REFERENTIAL_MAP_ID}
                    module={API_MODULE_ROADSIDE}
                    subNetworks={selectedSubNetworkSlugs}
                    roadToHide={road} />
                <RoadRoadsideInventoriesLayer
                    road={road}
                    type={type}
                    showSiblingTypes={!isCategoryType}
                    mode="presence"
                    mapId={REFERENTIAL_MAP_ID}
                    mapZoom={mapZoom}
                />
                {!!roadFeatures && <Draw
                    container={containerElement}
                    defaultMode={PAINT_MODE}
                    modesConfig={modesConfig}
                    lineColorStyle={stabiloLayerStyle}
                    opts={{ roadSegments: roadFeatures, startingFeatures: typeFeatures, existingFeatures: otherTypesFeatures?.features }}
                    onFeaturesChange={handleFeaturesChanged}
                />}
            </ReferentialMap>
        </UiModal>
    )
}